import { useState, useEffect, useCallback, useMemo } from 'react';
import { s, authHeaders, Toast, StandardDataGrid, gridActionButton } from './listPageStyles';

const API_BASE = '/api/surveys';

const TARGET_TYPES = [
  { key: 'Customer', label: 'Müşteri' },
  { key: 'Representative', label: 'Temsilci' },
];

const ACTIVE_TABS = [
  { key: '', label: 'Hepsi' },
  { key: 'true', label: 'Aktif' },
  { key: 'false', label: 'Pasif' },
];

const QUESTION_TYPES = [
  { key: 'Text', label: 'Metin' },
  { key: 'SingleChoice', label: 'Tek Seçim' },
  { key: 'MultiChoice', label: 'Çoklu Seçim' },
  { key: 'Numeric', label: 'Sayısal' },
  { key: 'Photo', label: 'Fotoğraf' },
];

const QUESTION_TYPE_LABEL = Object.fromEntries(QUESTION_TYPES.map(t => [t.key, t.label]));

const PAGE_SIZE_DEFAULT = 20;

const EMPTY_SURVEY_FORM = {
  textId: '',
  title: '',
  description: '',
  isActive: true,
  startDate: '',
  endDate: '',
  targetType: 'Customer',
};

const EMPTY_QUESTION_FORM = {
  id: null,
  sortOrder: 1,
  questionText: '',
  questionType: 'Text',
  isRequired: false,
  optionsJson: '',
  minValue: '',
  maxValue: '',
};

function pick(obj, ...keys) {
  if (!obj) return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
}

function unwrapList(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return {
    items: pick(data, 'items', 'Items') ?? [],
    activeCount: pick(data, 'activeCount', 'ActiveCount') ?? 0,
    inactiveCount: pick(data, 'inactiveCount', 'InactiveCount') ?? 0,
  };
}

function unwrapItems(payload) {
  const data = pick(payload, 'Data', 'data') ?? payload;
  return pick(data, 'items', 'Items') ?? [];
}

function formatDate(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return '-'; }
}

function formatDateTime(value) {
  if (!value) return '-';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('tr-TR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return '-'; }
}

// SurveyAnswer'ın metin gösterimi (foto ayrı render edilir). Çoktan seçmeli JSON
// dizisini "A, C" şeklinde birleştirir; numeric/text olduğu gibi döner.
function answerDisplayText(a) {
  const json = pick(a, 'answerJson', 'AnswerJson');
  if (json) {
    try {
      const arr = JSON.parse(json);
      if (Array.isArray(arr)) return arr.join(', ');
    } catch { /* düz metne düş */ }
    return String(json);
  }
  const numeric = pick(a, 'answerNumeric', 'AnswerNumeric');
  if (numeric !== undefined && numeric !== null) return String(numeric);
  const text = pick(a, 'answerText', 'AnswerText');
  if (text) return text;
  return '';
}

function toLocalDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  } catch { return ''; }
}

function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

function activePill(isActive) {
  if (isActive) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
        background: 'rgba(0, 200, 117, 0.12)', border: '1px solid rgba(0, 200, 117, 0.30)', color: '#0f5132',
      }}>
        <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
        Aktif
      </span>
    );
  }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '4px 10px', borderRadius: 999, fontWeight: 800, fontSize: 12,
      background: 'rgba(100, 116, 139, 0.14)', border: '1px solid rgba(100, 116, 139, 0.28)', color: '#64748b',
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: 'currentColor' }} />
      Pasif
    </span>
  );
}

function questionTypePill(type) {
  const tones = {
    Text: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569' },
    SingleChoice: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8' },
    MultiChoice: { bg: 'rgba(139, 92, 246, 0.12)', border: 'rgba(139, 92, 246, 0.30)', color: '#5b21b6' },
    Numeric: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e' },
    Photo: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.30)', color: '#0f5132' },
  };
  const t = tones[type] || tones.Text;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 999, fontWeight: 700, fontSize: 11,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
    }}>
      {QUESTION_TYPE_LABEL[type] || type || '-'}
    </span>
  );
}

function badge(label, value, tone) {
  const tones = {
    green: { bg: 'rgba(0, 200, 117, 0.12)', border: 'rgba(0, 200, 117, 0.32)', color: '#0f5132', dot: '#16a34a' },
    gray: { bg: 'rgba(100, 116, 139, 0.10)', border: 'rgba(100, 116, 139, 0.28)', color: '#475569', dot: '#64748b' },
    blue: { bg: 'rgba(0, 115, 234, 0.10)', border: 'rgba(0, 115, 234, 0.32)', color: '#1d4ed8', dot: 'var(--accent)' },
    orange: { bg: 'rgba(253, 171, 61, 0.14)', border: 'rgba(253, 171, 61, 0.32)', color: '#92400e', dot: 'var(--warn)' },
  };
  const t = tones[tone] || tones.gray;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '7px 12px', borderRadius: 999,
      background: t.bg, border: `1px solid ${t.border}`, color: t.color,
      fontWeight: 800, fontSize: 12,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: 999, background: t.dot }} />
      {label}: {value}
    </span>
  );
}

export function SurveysPageLegacy() {
  const [activeFilter, setActiveFilter] = useState('');
  const [targetFilter, setTargetFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ activeCount: 0, inactiveCount: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [sortField, setSortField] = useState('UpdatedDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedId, setSelectedId] = useState(null);

  // Survey edit modal
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [surveyForm, setSurveyForm] = useState(EMPTY_SURVEY_FORM);
  const [savingSurvey, setSavingSurvey] = useState(false);

  // Questions modal
  const [questionsForSurvey, setQuestionsForSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionForm, setQuestionForm] = useState(null);
  const [savingQuestion, setSavingQuestion] = useState(false);

  // Answers (read-only drill-down) modal
  const [answersForSurvey, setAnswersForSurvey] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [answersLoading, setAnswersLoading] = useState(false);
  const [answersStats, setAnswersStats] = useState(null);

  const [deleteSurveyRow, setDeleteSurveyRow] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const showToast = useCallback((type, message) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 3000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter) params.set('isActive', activeFilter);
      if (targetFilter) params.set('targetType', targetFilter);
      params.set('take', '500');
      const res = await fetch(`${API_BASE}?${params.toString()}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Liste alınamadı (${res.status})`);
      const payload = await res.json();
      const data = unwrapList(payload);
      setRows(Array.isArray(data.items) ? data.items : []);
      setCounts({ activeCount: data.activeCount, inactiveCount: data.inactiveCount });
      setPage(1);
    } catch (err) {
      showToast('error', err.message || 'Liste alınamadı');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, targetFilter, showToast]);

  useEffect(() => { load(); }, [load]);

  // ── Survey CRUD ──────────────────────────────────────────────────────────

  const openCreateSurvey = () => {
    setEditingSurvey({ mode: 'create' });
    setSurveyForm(EMPTY_SURVEY_FORM);
  };

  const openEditSurvey = (row) => {
    setEditingSurvey({ mode: 'edit', id: pick(row, 'id', 'Id') });
    setSurveyForm({
      textId: pick(row, 'textId', 'TextId') || '',
      title: pick(row, 'title', 'Title') || '',
      description: pick(row, 'description', 'Description') || '',
      isActive: pick(row, 'isActive', 'IsActive') ?? true,
      startDate: toLocalDateInput(pick(row, 'startDate', 'StartDate')),
      endDate: toLocalDateInput(pick(row, 'endDate', 'EndDate')),
      targetType: pick(row, 'targetType', 'TargetType') || 'Customer',
    });
  };

  const closeEditSurvey = () => { setEditingSurvey(null); setSurveyForm(EMPTY_SURVEY_FORM); };

  const submitSurvey = async () => {
    if (!surveyForm.textId.trim()) { showToast('error', 'TextId zorunlu.'); return; }
    if (!surveyForm.title.trim()) { showToast('error', 'Başlık zorunlu.'); return; }

    setSavingSurvey(true);
    try {
      const body = {
        textId: surveyForm.textId.trim(),
        title: surveyForm.title.trim(),
        description: surveyForm.description.trim() || null,
        isActive: !!surveyForm.isActive,
        startDate: fromLocalDateInput(surveyForm.startDate),
        endDate: fromLocalDateInput(surveyForm.endDate),
        targetType: surveyForm.targetType,
      };
      const url = editingSurvey.mode === 'edit' ? `${API_BASE}/${editingSurvey.id}` : API_BASE;
      const method = editingSurvey.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', editingSurvey.mode === 'edit' ? 'Anket güncellendi.' : 'Anket oluşturuldu.');
      closeEditSurvey();
      await load();
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSavingSurvey(false);
    }
  };

  const submitDeleteSurvey = async () => {
    if (!deleteSurveyRow) return;
    const id = pick(deleteSurveyRow, 'id', 'Id');
    setDeleteBusy(true);
    try {
      const res = await fetch(`${API_BASE}/${id}`, { method: 'DELETE', headers: authHeaders() });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Anket silindi (sorular cascade ile silindi).');
      setDeleteSurveyRow(null);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ── Questions modal ──────────────────────────────────────────────────────

  const openQuestions = async (survey) => {
    setQuestionsForSurvey(survey);
    setQuestionForm(null);
    setQuestions([]);
    await loadQuestions(pick(survey, 'id', 'Id'));
  };

  const loadQuestions = async (surveyId) => {
    if (!surveyId) return;
    setQuestionsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/${surveyId}/questions`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Sorular alınamadı (${res.status})`);
      const payload = await res.json();
      setQuestions(unwrapItems(payload));
    } catch (err) {
      showToast('error', err.message || 'Sorular alınamadı');
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const closeQuestions = () => {
    setQuestionsForSurvey(null);
    setQuestions([]);
    setQuestionForm(null);
  };

  // ── Answers modal (read-only drill-down) ───────────────────────────────────

  const openAnswers = async (survey) => {
    setAnswersForSurvey(survey);
    setAnswers([]);
    setAnswersStats(null);
    setAnswersLoading(true);
    try {
      const surveyId = pick(survey, 'id', 'Id');
      const res = await fetch(`${API_BASE}/${surveyId}/answers?take=1000`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`Cevaplar alınamadı (${res.status})`);
      const payload = await res.json();
      const data = pick(payload, 'Data', 'data') ?? payload;
      setAnswers(pick(data, 'items', 'Items') ?? []);
      setAnswersStats({
        total: pick(data, 'total', 'Total') ?? 0,
        respondentCount: pick(data, 'respondentCount', 'RespondentCount') ?? 0,
        customerCount: pick(data, 'customerCount', 'CustomerCount') ?? 0,
        questionCount: pick(data, 'questionCount', 'QuestionCount') ?? 0,
      });
    } catch (err) {
      showToast('error', err.message || 'Cevaplar alınamadı');
      setAnswers([]);
    } finally {
      setAnswersLoading(false);
    }
  };

  const closeAnswers = () => {
    setAnswersForSurvey(null);
    setAnswers([]);
    setAnswersStats(null);
  };

  const openAddQuestion = () => {
    const nextOrder = (questions.reduce((m, q) => Math.max(m, pick(q, 'sortOrder', 'SortOrder') || 0), 0)) + 1;
    setQuestionForm({ ...EMPTY_QUESTION_FORM, sortOrder: nextOrder, mode: 'create' });
  };

  const openEditQuestion = (q) => {
    setQuestionForm({
      id: pick(q, 'id', 'Id'),
      mode: 'edit',
      sortOrder: pick(q, 'sortOrder', 'SortOrder') ?? 1,
      questionText: pick(q, 'questionText', 'QuestionText') || '',
      questionType: pick(q, 'questionType', 'QuestionType') || 'Text',
      isRequired: pick(q, 'isRequired', 'IsRequired') ?? false,
      optionsJson: pick(q, 'optionsJson', 'OptionsJson') || '',
      minValue: pick(q, 'minValue', 'MinValue') ?? '',
      maxValue: pick(q, 'maxValue', 'MaxValue') ?? '',
    });
  };

  const submitQuestion = async () => {
    if (!questionForm) return;
    if (!questionForm.questionText.trim()) { showToast('error', 'Soru metni zorunlu.'); return; }
    if ((questionForm.questionType === 'SingleChoice' || questionForm.questionType === 'MultiChoice') &&
        !questionForm.optionsJson.trim()) {
      showToast('error', 'Seçenekli sorular için OptionsJson zorunlu.');
      return;
    }

    const surveyId = pick(questionsForSurvey, 'id', 'Id');
    setSavingQuestion(true);
    try {
      const body = {
        sortOrder: Number(questionForm.sortOrder) || 0,
        questionText: questionForm.questionText.trim(),
        questionType: questionForm.questionType,
        isRequired: !!questionForm.isRequired,
        optionsJson: questionForm.optionsJson.trim() || null,
        minValue: questionForm.minValue === '' ? null : Number(questionForm.minValue),
        maxValue: questionForm.maxValue === '' ? null : Number(questionForm.maxValue),
      };
      const url = questionForm.mode === 'edit'
        ? `${API_BASE}/${surveyId}/questions/${questionForm.id}`
        : `${API_BASE}/${surveyId}/questions`;
      const method = questionForm.mode === 'edit' ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers: authHeaders(), body: JSON.stringify(body) });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `İşlem başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', questionForm.mode === 'edit' ? 'Soru güncellendi.' : 'Soru eklendi.');
      setQuestionForm(null);
      await loadQuestions(surveyId);
      await load(); // questionCount güncelle
    } catch (err) {
      showToast('error', err.message || 'İşlem başarısız');
    } finally {
      setSavingQuestion(false);
    }
  };

  const deleteQuestion = async (q) => {
    if (!window.confirm('Bu soruyu silmek istediğinize emin misiniz?')) return;
    const surveyId = pick(questionsForSurvey, 'id', 'Id');
    const questionId = pick(q, 'id', 'Id');
    try {
      const res = await fetch(`${API_BASE}/${surveyId}/questions/${questionId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const payload = await res.json().catch(() => null);
      if (!res.ok) {
        const msg = pick(payload, 'Message', 'message') || `Silme başarısız (${res.status})`;
        throw new Error(msg);
      }
      showToast('success', 'Soru silindi.');
      await loadQuestions(surveyId);
      await load();
    } catch (err) {
      showToast('error', err.message || 'Silme başarısız');
    }
  };

  // ── Grid ─────────────────────────────────────────────────────────────────

  const filteredSorted = useMemo(() => {
    const needle = searchText.trim().toLocaleLowerCase('tr-TR');
    const match = (row) => {
      if (!needle) return true;
      const fields = [
        pick(row, 'textId', 'TextId'),
        pick(row, 'title', 'Title'),
        pick(row, 'description', 'Description'),
      ];
      return fields.some(v => v && String(v).toLocaleLowerCase('tr-TR').includes(needle));
    };
    const filtered = rows.filter(match);
    const dir = sortDir === 'desc' ? -1 : 1;
    const sortKey = (row) => {
      switch (sortField) {
        case 'Title': return pick(row, 'title', 'Title') || '';
        case 'TargetType': return pick(row, 'targetType', 'TargetType') || '';
        case 'QuestionCount': return Number(pick(row, 'questionCount', 'QuestionCount')) || 0;
        case 'UpdatedDate':
        default: {
          const v = pick(row, 'updatedDate', 'UpdatedDate');
          return v ? new Date(v).getTime() : 0;
        }
      }
    };
    return [...filtered].sort((a, b) => {
      const av = sortKey(a);
      const bv = sortKey(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [rows, searchText, sortField, sortDir]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredSorted.slice(start, start + pageSize);
  }, [filteredSorted, page, pageSize]);

  const columns = useMemo(() => [
    {
      field: 'title',
      headerName: 'Başlık',
      flex: 1.6,
      minWidth: 240,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.3 }}>
          <span style={{ fontWeight: 700 }}>{pick(row, 'title', 'Title') || '-'}</span>
          <span style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace' }}>
            {pick(row, 'textId', 'TextId') || '-'}
          </span>
        </div>
      ),
    },
    {
      field: 'targetType',
      headerName: 'Hedef',
      flex: 0.6,
      minWidth: 100,
      valueGetter: ({ row }) => {
        const t = pick(row, 'targetType', 'TargetType');
        return TARGET_TYPES.find(x => x.key === t)?.label || t || '-';
      },
    },
    {
      field: 'questionCount',
      headerName: 'Soru',
      flex: 0.4,
      minWidth: 70,
      valueGetter: ({ row }) => pick(row, 'questionCount', 'QuestionCount') ?? 0,
    },
    {
      field: 'period',
      headerName: 'Dönem',
      flex: 0.9,
      minWidth: 170,
      sortable: false,
      valueGetter: ({ row }) =>
        `${formatDate(pick(row, 'startDate', 'StartDate'))} – ${formatDate(pick(row, 'endDate', 'EndDate'))}`,
    },
    {
      field: 'isActive',
      headerName: 'Durum',
      flex: 0.6,
      minWidth: 100,
      renderCell: ({ row }) => activePill(pick(row, 'isActive', 'IsActive')),
    },
    {
      field: 'actions',
      headerName: 'Aksiyon',
      flex: 1.5,
      minWidth: 320,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <div style={{ display: 'flex', gap: 6 }} onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--accent)' }}
            onClick={() => openQuestions(row)}
          >
            Sorular
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#0d9488' }}
            onClick={() => openAnswers(row)}
          >
            Cevaplar
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: 'var(--warn)' }}
            onClick={() => openEditSurvey(row)}
          >
            Düzenle
          </button>
          <button
            type="button"
            style={{ ...gridActionButton, background: '#dc2626' }}
            onClick={() => setDeleteSurveyRow(row)}
          >
            Sil
          </button>
        </div>
      ),
    },
  ], []);

  const totalCount = filteredSorted.length;

  return (
    <div style={s.container}>
      <div style={s.pageHeader}>
        <div style={s.pageHeaderText}>
          <h1 style={s.pageTitle}>Anketler</h1>
          <p style={s.pageSubtitle}>
            Saha temsilcisine atanan anketler. Her anket çoklu soru içerir; mobile
            master delta ile çeker, cevaplar push ile gelir.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {badge('Aktif', counts.activeCount, 'green')}
          {badge('Pasif', counts.inactiveCount, 'gray')}
          <button type="button" style={s.btnSecondary} onClick={load} disabled={loading}>
            {loading ? 'Yükleniyor...' : 'Yenile'}
          </button>
          <button type="button" style={s.btnPrimary} onClick={openCreateSurvey}>
            + Yeni Anket
          </button>
        </div>
      </div>

      <div style={s.toolbar}>
        <div style={s.toolbarLeft}>
          {ACTIVE_TABS.map(tab => (
            <button
              key={tab.key || 'all'}
              type="button"
              onClick={() => setActiveFilter(tab.key)}
              style={{
                ...(activeFilter === tab.key ? s.btnPrimary : s.btnSecondary),
                padding: '6px 14px', fontSize: 13,
              }}
            >
              {tab.label}
            </button>
          ))}
          <span style={{ width: 1, height: 22, background: '#e5e7eb', margin: '0 4px' }} />
          <select
            value={targetFilter}
            onChange={(e) => setTargetFilter(e.target.value)}
            style={s.select}
          >
            <option value="">Tüm Hedefler</option>
            {TARGET_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
        </div>
        <div style={s.toolbarRight}>
          <input
            type="search"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            placeholder="Başlık / textId / açıklama..."
            style={s.filterInput}
          />
        </div>
      </div>

      <StandardDataGrid
        rows={pagedRows}
        columns={columns}
        totalCount={totalCount}
        loading={loading}
        pageSize={pageSize}
        currentPage={page}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        sortField={sortField}
        sortDir={sortDir}
        onSortChange={(field, dir) => { setSortField(field); setSortDir(dir); }}
        selectedId={selectedId}
        onRowClick={(row) => setSelectedId(pick(row, 'id', 'Id'))}
        onRowDoubleClick={openQuestions}
        exportFileName="anketler"
      />

      {/* ── Survey edit modal ─────────────────────────────────────────────── */}
      {editingSurvey && (
        <div style={s.overlay} onClick={closeEditSurvey}>
          <div style={{ ...s.modal, maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                {editingSurvey.mode === 'edit' ? 'Anketi Düzenle' : 'Yeni Anket'}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeEditSurvey}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>TextId *</label>
                  <input
                    type="text"
                    value={surveyForm.textId}
                    onChange={(e) => setSurveyForm({ ...surveyForm, textId: e.target.value })}
                    placeholder="örn. tr-satisfaction-2026"
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Hedef *</label>
                  <select
                    value={surveyForm.targetType}
                    onChange={(e) => setSurveyForm({ ...surveyForm, targetType: e.target.value })}
                    style={s.select}
                  >
                    {TARGET_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Başlık *</label>
                <input
                  type="text"
                  value={surveyForm.title}
                  onChange={(e) => setSurveyForm({ ...surveyForm, title: e.target.value })}
                  style={s.input}
                />
              </div>
              <div style={s.formGroup}>
                <label style={s.label}>Açıklama</label>
                <textarea
                  rows={3}
                  value={surveyForm.description}
                  onChange={(e) => setSurveyForm({ ...surveyForm, description: e.target.value })}
                  style={s.textarea}
                />
              </div>
              <div style={s.formRow}>
                <div style={s.formGroup}>
                  <label style={s.label}>Başlangıç</label>
                  <input
                    type="date"
                    value={surveyForm.startDate}
                    onChange={(e) => setSurveyForm({ ...surveyForm, startDate: e.target.value })}
                    style={s.input}
                  />
                </div>
                <div style={s.formGroup}>
                  <label style={s.label}>Bitiş</label>
                  <input
                    type="date"
                    value={surveyForm.endDate}
                    onChange={(e) => setSurveyForm({ ...surveyForm, endDate: e.target.value })}
                    style={s.input}
                  />
                </div>
              </div>
              <div style={s.formGroup}>
                <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={surveyForm.isActive}
                    onChange={(e) => setSurveyForm({ ...surveyForm, isActive: e.target.checked })}
                  />
                  Aktif (mobile'da görünür)
                </label>
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeEditSurvey} disabled={savingSurvey}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnPrimary, opacity: savingSurvey ? 0.6 : 1 }}
                onClick={submitSurvey}
                disabled={savingSurvey}
              >
                {savingSurvey ? 'Kaydediliyor...' : (editingSurvey.mode === 'edit' ? 'Güncelle' : 'Oluştur')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Questions modal ───────────────────────────────────────────────── */}
      {questionsForSurvey && (
        <div style={s.overlay} onClick={closeQuestions}>
          <div style={{ ...s.modal, maxWidth: 880 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                Sorular — {pick(questionsForSurvey, 'title', 'Title')}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeQuestions}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <div style={{ fontSize: 13, color: '#6b7280' }}>
                  {questions.length} soru
                  {questionsLoading && ' (yükleniyor...)'}
                </div>
                {!questionForm && (
                  <button type="button" style={s.btnPrimary} onClick={openAddQuestion}>
                    + Yeni Soru
                  </button>
                )}
              </div>

              {/* Question list */}
              {!questionForm && (
                <div style={{
                  border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                  maxHeight: 360, overflowY: 'auto',
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ background: '#f9fafb' }}>
                      <tr>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280', width: 50 }}>#</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Soru</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Tip</th>
                        <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Zorunlu</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280', width: 180 }}>Aksiyon</th>
                      </tr>
                    </thead>
                    <tbody>
                      {questions.length === 0 && (
                        <tr><td colSpan="5" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Henüz soru yok</td></tr>
                      )}
                      {questions.map((q, idx) => (
                        <tr key={pick(q, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                          <td style={{ padding: '8px 12px', color: '#6b7280' }}>{pick(q, 'sortOrder', 'SortOrder')}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <div style={{ fontWeight: 600 }}>{pick(q, 'questionText', 'QuestionText') || '-'}</div>
                            {pick(q, 'optionsJson', 'OptionsJson') && (
                              <div style={{ fontSize: 11, color: '#6b7280', fontFamily: 'monospace', marginTop: 2 }}>
                                {String(pick(q, 'optionsJson', 'OptionsJson')).slice(0, 80)}
                                {String(pick(q, 'optionsJson', 'OptionsJson')).length > 80 ? '…' : ''}
                              </div>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px' }}>
                            {questionTypePill(pick(q, 'questionType', 'QuestionType'))}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                            {pick(q, 'isRequired', 'IsRequired') ? '✓' : ''}
                          </td>
                          <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                            <button
                              type="button"
                              style={{ ...gridActionButton, background: 'var(--warn)', marginRight: 6 }}
                              onClick={() => openEditQuestion(q)}
                            >
                              Düzenle
                            </button>
                            <button
                              type="button"
                              style={{ ...gridActionButton, background: '#dc2626' }}
                              onClick={() => deleteQuestion(q)}
                            >
                              Sil
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Question form */}
              {questionForm && (
                <div style={{
                  border: '1px solid var(--accent)', borderRadius: 10, padding: 16,
                  background: 'rgba(0, 115, 234, 0.04)',
                }}>
                  <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 12 }}>
                    {questionForm.mode === 'edit' ? 'Soruyu Düzenle' : 'Yeni Soru'}
                  </div>
                  <div style={s.formRow}>
                    <div style={s.formGroup}>
                      <label style={s.label}>Sıra</label>
                      <input
                        type="number"
                        min="0"
                        value={questionForm.sortOrder}
                        onChange={(e) => setQuestionForm({ ...questionForm, sortOrder: e.target.value })}
                        style={s.input}
                      />
                    </div>
                    <div style={s.formGroup}>
                      <label style={s.label}>Tip *</label>
                      <select
                        value={questionForm.questionType}
                        onChange={(e) => setQuestionForm({ ...questionForm, questionType: e.target.value })}
                        style={s.select}
                      >
                        {QUESTION_TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={s.formGroup}>
                    <label style={s.label}>Soru Metni *</label>
                    <textarea
                      rows={2}
                      value={questionForm.questionText}
                      onChange={(e) => setQuestionForm({ ...questionForm, questionText: e.target.value })}
                      style={s.textarea}
                    />
                  </div>
                  {(questionForm.questionType === 'SingleChoice' || questionForm.questionType === 'MultiChoice') && (
                    <div style={s.formGroup}>
                      <label style={s.label}>Seçenekler (JSON dizi) *</label>
                      <textarea
                        rows={2}
                        value={questionForm.optionsJson}
                        onChange={(e) => setQuestionForm({ ...questionForm, optionsJson: e.target.value })}
                        placeholder='["Çok memnun","Memnun","Nötr","Memnun değil"]'
                        style={{ ...s.textarea, fontFamily: 'monospace', fontSize: 12 }}
                      />
                    </div>
                  )}
                  {questionForm.questionType === 'Numeric' && (
                    <div style={s.formRow}>
                      <div style={s.formGroup}>
                        <label style={s.label}>Min</label>
                        <input
                          type="number"
                          value={questionForm.minValue}
                          onChange={(e) => setQuestionForm({ ...questionForm, minValue: e.target.value })}
                          style={s.input}
                        />
                      </div>
                      <div style={s.formGroup}>
                        <label style={s.label}>Max</label>
                        <input
                          type="number"
                          value={questionForm.maxValue}
                          onChange={(e) => setQuestionForm({ ...questionForm, maxValue: e.target.value })}
                          style={s.input}
                        />
                      </div>
                    </div>
                  )}
                  <div style={s.formGroup}>
                    <label style={{ ...s.label, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={questionForm.isRequired}
                        onChange={(e) => setQuestionForm({ ...questionForm, isRequired: e.target.checked })}
                      />
                      Zorunlu
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 12 }}>
                    <button type="button" style={s.btnSecondary} onClick={() => setQuestionForm(null)} disabled={savingQuestion}>
                      Vazgeç
                    </button>
                    <button
                      type="button"
                      style={{ ...s.btnPrimary, opacity: savingQuestion ? 0.6 : 1 }}
                      onClick={submitQuestion}
                      disabled={savingQuestion}
                    >
                      {savingQuestion ? 'Kaydediliyor...' : (questionForm.mode === 'edit' ? 'Güncelle' : 'Ekle')}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeQuestions}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Answers modal (read-only drill-down) ──────────────────────────── */}
      {answersForSurvey && (
        <div style={s.overlay} onClick={closeAnswers}>
          <div style={{ ...s.modal, maxWidth: 960 }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>
                Cevaplar — {pick(answersForSurvey, 'title', 'Title')}
              </h2>
              <button type="button" style={s.closeBtn} onClick={closeAnswers}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
                {badge('Cevap', answersStats?.total ?? 0, 'blue')}
                {badge('Temsilci', answersStats?.respondentCount ?? 0, 'green')}
                {badge('Müşteri', answersStats?.customerCount ?? 0, 'orange')}
                {badge('Soru', answersStats?.questionCount ?? 0, 'gray')}
              </div>

              {answersLoading && <div style={{ color: '#6b7280', fontSize: 13 }}>Yükleniyor...</div>}

              {!answersLoading && (
                <div style={{
                  border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden',
                  maxHeight: 440, overflowY: 'auto',
                }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ background: '#f9fafb', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Soru</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Cevap</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Müşteri</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Temsilci</th>
                        <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 700, fontSize: 11, color: '#6b7280' }}>Tarih</th>
                      </tr>
                    </thead>
                    <tbody>
                      {answers.length === 0 && (
                        <tr><td colSpan="5" style={{ padding: 24, textAlign: 'center', color: '#94a3b8' }}>Henüz cevap yok</td></tr>
                      )}
                      {answers.map((a, idx) => {
                        const photo = pick(a, 'photoUrl', 'PhotoUrl');
                        const text = answerDisplayText(a);
                        return (
                          <tr key={pick(a, 'id', 'Id') || idx} style={{ borderTop: idx > 0 ? '1px solid #f3f4f6' : 'none' }}>
                            <td style={{ padding: '8px 12px' }}>
                              <div style={{ fontWeight: 600 }}>{pick(a, 'questionText', 'QuestionText') || '-'}</div>
                              {pick(a, 'questionType', 'QuestionType') && (
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>{pick(a, 'questionType', 'QuestionType')}</div>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px' }}>
                              {photo ? (
                                <a href={photo} target="_blank" rel="noreferrer"
                                   style={{ display: 'inline-block', width: 56, height: 56, borderRadius: 8, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                                  <img src={photo} alt="Cevap" style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                       onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.style.background = '#f3f4f6'; }} />
                                </a>
                              ) : (
                                <span style={{ fontWeight: 600 }}>{text || <span style={{ color: '#94a3b8' }}>-</span>}</span>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', color: '#475569' }}>{pick(a, 'customerTitle', 'CustomerTitle') || '-'}</td>
                            <td style={{ padding: '8px 12px', color: '#475569' }}>{pick(a, 'representativeName', 'RepresentativeName') || '-'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'right', color: '#6b7280', whiteSpace: 'nowrap' }}>
                              {formatDateTime(pick(a, 'answeredAt', 'AnsweredAt'))}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={closeAnswers}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete confirm ────────────────────────────────────────────────── */}
      {deleteSurveyRow && (
        <div style={s.overlay} onClick={() => setDeleteSurveyRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>Anketi Sil</h2>
              <button type="button" style={s.closeBtn} onClick={() => setDeleteSurveyRow(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 14, color: '#1f2937' }}>
                <strong>{pick(deleteSurveyRow, 'title', 'Title')}</strong> anketini silmek istediğinize emin misiniz?
              </div>
              <div style={{ fontSize: 12, color: '#dc2626', marginTop: 8 }}>
                Anket ve tüm soruları (cascade) silinir. Mevcut cevaplar tabloda dangling kalabilir.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button type="button" style={s.btnSecondary} onClick={() => setDeleteSurveyRow(null)} disabled={deleteBusy}>
                Vazgeç
              </button>
              <button
                type="button"
                style={{ ...s.btnDanger, opacity: deleteBusy ? 0.6 : 1 }}
                onClick={submitDeleteSurvey}
                disabled={deleteBusy}
              >
                {deleteBusy ? 'Siliniyor...' : 'Sil'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
}

export default SurveysPageLegacy;
